import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { BaselineService } from '../baseline.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Product, userProductEntry } from 'src/app/shared/models/interfaces';

@Component({
  selector: 'app-digitalization',
  imports: [UIComponentsModule],
  templateUrl: './digitalization.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './digitalization.component.scss',
})
export class DigitalizationComponent {
  private pendingChanges: userProductEntry[] = [];

  constructor(
    private baselineService: BaselineService,
    private snackbarService: SnackbarService
  ) {}

  @Input() products: Product[] = [];
  @Input() userEntries: userProductEntry[] = [];
  @Output() answerChanged = new EventEmitter<void>();

  getYearForProduct(productId: number): string | null {
    const year = this.userEntries.find((e) => e.productId === productId)?.year;
    return year === undefined
      ? null
      : `${year} ${year === 1 ? 'year' : 'years'}`;
  }

  decrementYear(productId: number) {
    const entry = this.userEntries.find((e) => e.productId === productId);
    if (entry && entry.year > 0) {
      entry.year--;
      // Don't remove locally yet, let the sync handle deletion
    } else {
      this.userEntries.push({
        productId: productId,
        year: 0,
      });
    }
    this.trackPendingChange(productId);
  }

  incrementYear(productId: number) {
    const entry = this.userEntries.find((e) => e.productId === productId);
    if (entry) {
      entry.year++;
    } else {
      this.userEntries.push({
        productId: productId,
        year: 1,
      });
    }
    this.trackPendingChange(productId);
  }

  private trackPendingChange(productId: number) {
    const entry = this.userEntries.find((e) => e.productId === productId);
    if (entry) {
      const existingChangeIndex = this.pendingChanges.findIndex(
        (e) => e.productId === productId
      );
      if (existingChangeIndex !== -1) {
        this.pendingChanges[existingChangeIndex] = { ...entry };
      } else {
        this.pendingChanges.push({ ...entry });
      }
    }

    // Sync changes immediately with a small delay
    setTimeout(() => this.syncPendingChanges(), 500);
  }

  private syncPendingChanges() {
    const changesToSync = [...this.pendingChanges];
    this.pendingChanges = [];

    changesToSync.forEach((entry) => {
      // Calculate caused emissions
      const product = this.products.find((p) => p.id === entry.productId);
      if (!product) {
        console.error('Product not found for entry with ID:', entry.productId);
        return;
      }

      if (entry.year < 0) {
        console.error('Year cannot be negative for entry with ID:', entry.productId);
        return;
      }

      if (entry.year > 0) {
        entry.causedEmissions = product.co2PerItem / entry.year;

        if (entry.id) {
          // Update existing entry
          entry.updatedAt = new Date();
          this.baselineService.updateUserProductEntry(entry).subscribe({
            next: (userEntry: userProductEntry) => {
              const index = this.userEntries.findIndex(
                (e) => e.id === userEntry.id
              );
              if (index !== -1) {
                this.userEntries[index] = userEntry;
              }
              this.answerChanged.emit();
            },
            error: (err) => {
              console.error('Error updating product entry:', err);
              this.snackbarService.showErrorMessage(
                'Failed to update product entry. Please try again.'
              );
            },
          });
        } else {
          // Create new entry
          this.baselineService.createUserProductEntry(entry).subscribe({
            next: (userEntry: userProductEntry) => {
              const index = this.userEntries.findIndex(
                (e) => e.productId === userEntry.productId
              );
              if (index !== -1) {
                this.userEntries[index] = userEntry;
              } else {
                this.userEntries.push(userEntry);
              }
              this.answerChanged.emit();
            },
            error: (err) => {
              console.error('Error creating product entry:', err);
              this.snackbarService.showErrorMessage(
                'Failed to create product entry. Please try again.'
              );
            },
          });
        }
      } else if (entry.year === 0 && entry.id) {
        // Delete entry if year is 0 and entry exists in database
        this.baselineService.deleteUserProductEntry(entry.id).subscribe({
          next: () => {
            // Remove from local array only after successful deletion
            this.userEntries = this.userEntries.filter(
              (e) => e.productId !== entry.productId
            );
            this.answerChanged.emit();
          },
          error: (err) => {
            console.error('Error deleting product entry:', err);
            this.snackbarService.showErrorMessage(
              'Failed to delete product entry. Please try again.'
            );
          },
        });
      } else if (entry.year === 0 && !entry.id) {
        // Remove locally if it's a new entry that was never saved
        this.userEntries = this.userEntries.filter(
          (e) => e.productId !== entry.productId
        );
      }
    });
  }
}
